import React, { useState, useEffect } from 'react';
import { ArrowLeft, Clock, User, Calendar, Search, ChevronRight, Loader2 } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import VODFooter from '../components/VODFooter';
import { blogArticles as staticArticles } from '../data/blogArticles';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const VODBlog = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [blogArticles, setBlogArticles] = useState(staticArticles);
  const [loading, setLoading] = useState(true);
  
  // Fetch articles from database
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await axios.get(`${API}/api/blog/articles?published_only=true`);
        if (res.data && res.data.length > 0) {
          setBlogArticles(res.data);
        }
        // If no DB articles, keep using static articles
      } catch (err) {
        console.log('Using static blog articles');
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  // Get unique categories
  const categories = ['all', ...new Set(blogArticles.map(article => article.category))];

  // Filter articles
  const filteredArticles = blogArticles.filter(article => {
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Featured article (first one)
  const featuredArticle = blogArticles[0];
  const remainingArticles = filteredArticles.filter(a => a.id !== featuredArticle.id);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>Blog | LTV VOD - Streaming News, Tips & Industry Insights</title>
        <meta name="description" content="Explore the LTV VOD blog for the latest streaming industry news, creator tips, Caribbean culture insights, and entertainment guides." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://ltvproduction.com/vod/blog" />
        <meta property="og:title" content="LTV VOD Blog - Streaming News & Insights" />
        <meta property="og:description" content="Stay updated with streaming industry trends, creator guides, and Caribbean culture content." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back
          </button>
          <Link to="/vod" className="flex items-center gap-2">
            <img src="/ltv-icon.png" alt="LTV VOD" className="h-8 w-8 brightness-0 invert" />
            <span className="font-bold">LTV VOD</span>
          </Link>
        </div>
      </header>
      
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-purple-900/20 to-transparent py-12">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">LTV VOD Blog</h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
            Insights, guides, and stories from the world of streaming entertainment
          </p>
          
          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-8 justify-center">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                  selectedCategory === category
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-900 text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
              >
                {category === 'all' ? 'All Articles' : category}
              </button>
            ))}
          </div>

          {/* Featured Article */}
          {selectedCategory === 'all' && searchQuery === '' && (
            <Link 
              to={`/vod/blog/${featuredArticle.slug}`}
              className="block mb-12 group"
            >
              <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/20">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="aspect-video md:aspect-auto">
                    <img 
                      src={featuredArticle.image} 
                      alt={featuredArticle.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6 md:p-8 flex flex-col justify-center">
                    <span className="text-purple-400 text-sm font-medium mb-2">Featured Article</span>
                    <h2 className="text-2xl md:text-3xl font-bold mb-4 group-hover:text-purple-400 transition-colors">
                      {featuredArticle.title}
                    </h2>
                    <p className="text-gray-400 mb-4 line-clamp-3">
                      {featuredArticle.excerpt}
                    </p>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        {featuredArticle.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {new Date(featuredArticle.date || featuredArticle.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {featuredArticle.readTime || featuredArticle.read_time}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          )}

          {/* Article Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(selectedCategory === 'all' && searchQuery === '' ? remainingArticles : filteredArticles).map(article => (
              <Link 
                key={article.id}
                to={`/vod/blog/${article.slug}`}
                className="group bg-gray-900/50 rounded-xl overflow-hidden border border-gray-800 hover:border-purple-500/50 transition-colors"
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <span className="text-purple-400 text-xs font-medium">{article.category}</span>
                  <h3 className="text-lg font-semibold mt-2 mb-2 group-hover:text-purple-400 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-gray-400 text-sm line-clamp-2 mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(article.date || article.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {article.readTime || article.read_time}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-400">No articles found matching your criteria.</p>
            </div>
          )}

          {/* Newsletter CTA */}
          <div className="mt-16 text-center bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-2xl p-8 border border-purple-500/20">
            <h2 className="text-2xl font-bold mb-4">Stay Updated</h2>
            <p className="text-gray-400 mb-6 max-w-xl mx-auto">
              Get the latest streaming tips, industry news, and exclusive content delivered to your inbox.
            </p>
            <Link
              to="/vod"
              className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg transition-colors"
            >
              Explore Our Content
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </main>
      
      <VODFooter />
    </div>
  );
};

export default VODBlog;
